import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { KeenIcon } from '@/components';
import { ColorInput } from '@/components/ui/input/ColorInput';
import { Checkbox, ConfigProvider, Select } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faThumbsDown, faCopy, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import BtnSubmitAuth from '@/components/ui/btnSubmitAuth';
import { Modal as AntdModal } from 'antd';
import { useAuth } from '../../../../../../hooks/useAuth';
import { UpdateAgentMutation } from '../../../../../../hooks/useAgentBuilder';
import { useGetAgent } from '../../../../../../hooks/useAgent';

const { Option } = Select;

const DEFAULTS = {
  fill: '#F2F2F2',
  stroke: '#50B553',
  suggest: '#0B660E',
  suggestOverlay: '#ECF8EC',
  button: '#50B553',
  buttonOverlay: '#ECF8EC',
  font: 'Tahoma',
  font_styling: 'Normal',
  font_size: '16',
  heart: false,
  dislike: false,
  copy: false,
};

const FONT_OPTIONS = [
  'Tahoma', 'Arial', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins', 'Inter', 'Times New Roman', 'Courier New', 'Georgia', 'Verdana', 'Trebuchet MS'
];
const FONT_STYLING_OPTIONS = [
  'Normal', 'Bold', 'Italic', 'Underline', 'Bold Italic', 'Bold Underline', 'Italic Underline', 'Bold Italic Underline'
];
const FONT_SIZE_OPTIONS = ['12', '14', '16', '18', '20', '24', '28', '32'];

const AgentAdvancedSettings = ({
  onBack,
  fill,
  stroke,
  suggest,
  suggestOverlay,
  button,
  buttonOverlay,
  font,
  font_styling,
  font_size,
  heart: heartProp,
  dislike: dislikeProp,
  copy: copyProp,
  onChange,
}) => {

  const { userData } = useAuth()
  const { data: agent } = useGetAgent(userData?.uID)
  const updateAgentMutation = UpdateAgentMutation()

  const mapAgentToSettings = (agentData) => {
    const a = agentData?.data ?? {};
    return {
      fill: a.fill ?? DEFAULTS.fill,
      stroke: a.stroke ?? DEFAULTS.stroke,
      suggest: a.suggest_color ?? DEFAULTS.suggest,
      suggestOverlay: a.suggest_overlay ?? DEFAULTS.suggestOverlay,
      button: a.button_color ?? DEFAULTS.button,
      buttonOverlay: a.button_overlay ?? DEFAULTS.buttonOverlay,
      font: a.font ?? DEFAULTS.font,
      font_styling: a.font_styling ?? DEFAULTS.font_styling,
      // lưu string để khớp Select
      font_size: String(a.font_size ?? DEFAULTS.font_size),
      heart: !!a.heart,
      dislike: !!a.dislike,
      copy: !!a.copy,
    };
  };

  const areSettingsEqual = (s1, s2) => {
    if (!s1 || !s2) return false;
    const keys = [
      'fill', 'stroke', 'suggest', 'suggestOverlay', 'button', 'buttonOverlay',
      'font', 'font_styling', 'font_size', 'heart', 'dislike', 'copy'
    ];
    return keys.every((k) => {
      if (k === 'font_size') return String(s1[k]) === String(s2[k]);
      return s1[k] === s2[k];
    });
  };

  const [settings, setSettings] = useState({
    fill: fill ?? DEFAULTS.fill,
    stroke: stroke ?? DEFAULTS.stroke,
    suggest: suggest ?? DEFAULTS.suggest,
    suggestOverlay: suggestOverlay ?? DEFAULTS.suggestOverlay,
    button: button ?? DEFAULTS.button,
    buttonOverlay: buttonOverlay ?? DEFAULTS.buttonOverlay,
    font: font ?? DEFAULTS.font,
    font_styling: font_styling ?? DEFAULTS.font_styling,
    font_size: font_size ?? DEFAULTS.font_size,
    heart: !!heartProp,
    dislike: !!dislikeProp,
    copy: !!copyProp,
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [showDefaultConfirmModal, setShowDefaultConfirmModal] = useState(false);
  const [fakeTestColor, setFakeTestColor] = useState('#AABBCC');

  // Kiểm tra xem settings hiện tại có bằng DEFAULTS không
  const isCurrentSettingsDefault = () => {
    return (
      settings.fill === DEFAULTS.fill &&
      settings.stroke === DEFAULTS.stroke &&
      settings.suggest === DEFAULTS.suggest &&
      settings.suggestOverlay === DEFAULTS.suggestOverlay &&
      settings.button === DEFAULTS.button &&
      settings.buttonOverlay === DEFAULTS.buttonOverlay &&
      settings.font === DEFAULTS.font &&
      settings.font_styling === DEFAULTS.font_styling &&
      settings.font_size === DEFAULTS.font_size &&
      settings.heart === DEFAULTS.heart &&
      settings.dislike === DEFAULTS.dislike &&
      settings.copy === DEFAULTS.copy
    );
  };

  // Reset settings khi props đổi (sau khi save) và tính hasChanges dựa trên agent
  useEffect(() => {
    const newSettings = {
      fill: fill ?? DEFAULTS.fill,
      stroke: stroke ?? DEFAULTS.stroke,
      suggest: suggest ?? DEFAULTS.suggest,
      suggestOverlay: suggestOverlay ?? DEFAULTS.suggestOverlay,
      button: button ?? DEFAULTS.button,
      buttonOverlay: buttonOverlay ?? DEFAULTS.buttonOverlay,
      font: font ?? DEFAULTS.font,
      font_styling: font_styling ?? DEFAULTS.font_styling,
      font_size: font_size ?? DEFAULTS.font_size,
      heart: !!heartProp,
      dislike: !!dislikeProp,
      copy: !!copyProp,
    };

    // Chỉ cập nhật khi thực sự có thay đổi
    if (JSON.stringify(newSettings) !== JSON.stringify(settings)) {
      setSettings(newSettings);
      const base = mapAgentToSettings(agent);
      setHasChanges(!areSettingsEqual(newSettings, base));
    }
  }, [fill, stroke, suggest, suggestOverlay, button, buttonOverlay, font, font_styling, font_size, heartProp, dislikeProp, copyProp, agent]);

  // Khi agent thay đổi, tính lại hasChanges dựa trên baseline agent
  useEffect(() => {
    if (!agent) return;
    const base = mapAgentToSettings(agent);
    setHasChanges(!areSettingsEqual(settings, base));
  }, [agent]);

  const handleChange = (key, value) => {
    setSettings(prev => {
      const next = { ...prev, [key]: value };
      const base = mapAgentToSettings(agent);
      setHasChanges(!areSettingsEqual(next, base));
      onChange?.(next);
      console.log("đang onchange");
      return next;
    });
  };

  const handleDefaultClick = () => {
    setShowDefaultConfirmModal(true);
  };

  const handleDefaultConfirm = () => {
    // Map DEFAULTS -> payload API theo định dạng backend
    const payload = {
      fill: DEFAULTS.fill,
      stroke: DEFAULTS.stroke,
      font: DEFAULTS.font,
      font_styling: DEFAULTS.font_styling,
      font_size: Number(DEFAULTS.font_size),
      heart: DEFAULTS.heart ? 1 : 0,
      dislike: DEFAULTS.dislike ? 1 : 0,
      copy: DEFAULTS.copy ? 1 : 0,
      button_color: DEFAULTS.button,
      button_overlay: DEFAULTS.buttonOverlay,
      suggest_color: DEFAULTS.suggest,
      suggest_overlay: DEFAULTS.suggestOverlay,
    };

    updateAgentMutation.mutate(payload);
    
    // Cập nhật local state
    const next = { ...DEFAULTS };
    setSettings(next);
    const base = mapAgentToSettings(agent);
    setHasChanges(!areSettingsEqual(next, base));
    onChange?.(next);
    
    setShowDefaultConfirmModal(false);
  };

  const handleDefaultCancel = () => {
    setShowDefaultConfirmModal(false);
  };

  const handleSave = () => {
    // Map state -> payload API theo định dạng backend
    const payload = {
      fill: settings.fill,
      stroke: settings.stroke,
      font: settings.font,
      font_styling: settings.font_styling,
      font_size: Number(settings.font_size),
      heart: settings.heart ? 1 : 0,
      dislike: settings.dislike ? 1 : 0,
      copy: settings.copy ? 1 : 0,
      button_color: settings.button,
      button_overlay: settings.buttonOverlay,
      suggest_color: settings.suggest,
      suggest_overlay: settings.suggestOverlay,
    };

    updateAgentMutation.mutate(payload);
    onChange?.(settings);
    setHasChanges(false);
  };

  // Xử lý nút quay lại
  const handleBack = () => {
    onBack();
  };

  return (
    <div>
      {/* Nút quay lại và tiêu đề */}
      <div className="flex items-center mb-2">
        <button onClick={handleBack} className="mr-2 flex items-center">
          <KeenIcon icon="arrow-left" className="text-lg" />
        </button>
        <span className="text-sm font-bold text-[#626262]"><FormattedMessage id="AGENTS.HOME.ADVANCED_SETTINGS" /></span>
      </div>

      {/* Row: Typography */}
      <div className="mb-2">
        <label className="block mb-3 font-medium text-sm"><FormattedMessage id="AGENTS.HOME.TYPOGRAPHY" /></label>
        <div className="grid grid-cols-4 gap-5">
          <ConfigProvider
            theme={{
              token: {
                fontSize: 12,
                borderRadius: "4px",
                controlItemBgActive: '#50b5532e', // nền khi chọn
              },
            }}
          >
            <Select
              className="col-span-4 rounded-[2px]"
              value={settings.font}
              onChange={val => handleChange('font', val)}
              size="large"
              style={{ width: '100%' }}
              suffixIcon={<FontAwesomeIcon icon={faChevronDown} className='pr-3 size-3' />}
            >
              {FONT_OPTIONS.map(f => <Option key={f} value={f} >{f}</Option>)}
            </Select>

            <Select
              className="col-span-2"
              value={settings.font_styling}
              onChange={val => handleChange('font_styling', val)}
              size="large"
              style={{ width: '100%' }}
              suffixIcon={<FontAwesomeIcon icon={faChevronDown} className='pr-3 size-3' />}

            >
              {FONT_STYLING_OPTIONS.map(f => <Option key={f} value={f}>{f}</Option>)}
            </Select>

            <Select
              className="col-span-2"
              value={settings.font_size}
              onChange={val => handleChange('font_size', val)}
              size="large"
              style={{ width: '100%' }}
              suffixIcon={<FontAwesomeIcon icon={faChevronDown} className='pr-3 size-3' />}

            >
              {FONT_SIZE_OPTIONS.map(f => <Option key={f} value={f}>{f}</Option>)}
            </Select>
          </ConfigProvider>
        </div>

      </div>

      {/* Row: Fill & Stroke */}
      <div className="mb-4">
        <div className="grid grid-cols-4 gap-x-5 gap-y-3">
          <div className='col-span-2 font-medium text-sm'>
            <ColorInput label={<FormattedMessage id="AGENTS.HOME.FILL" />} value={settings.fill} onChange={val => handleChange('fill', val)} />
          </div>
          <div className='col-span-2 text-sm font-medium'>
            <ColorInput label={<FormattedMessage id="AGENTS.HOME.STROKE" />} value={settings.stroke} onChange={val => handleChange('stroke', val)} />
          </div>
          <div className='col-span-2 text-sm font-medium'>
            <ColorInput label={<FormattedMessage id="AGENTS.HOME.SUGGEST" />} value={settings.suggest} onChange={val => handleChange('suggest', val)} />
          </div>
          <div className='col-span-2 text-sm font-medium'>
            <ColorInput label={<FormattedMessage id="AGENTS.HOME.SUGGEST_OVERLAY" />} value={settings.suggestOverlay} onChange={val => handleChange('suggestOverlay', val)} />
          </div>
          <div className='col-span-2 text-sm font-medium'>
            <ColorInput label={<FormattedMessage id="AGENTS.HOME.BUTTON" />} value={settings.button} onChange={val => handleChange('button', val)} />
          </div>
          <div className='col-span-2 text-sm font-medium'>
            <ColorInput label={<FormattedMessage id="AGENTS.HOME.BUTTON_OVERLAY" />} value={settings.buttonOverlay} onChange={val => handleChange('buttonOverlay', val)} />
          </div>
        </div>
      </div>

      {/* Row: Icon */}
      <div className="mb-8 grid grid-cols-3">
        <div className="col-span-2 grid grid-cols-3 gap-4 text-2xl">
          <div className='flex items-center gap-2 justify-center'>
            <FontAwesomeIcon icon={faHeart} className="text-red-500 size-5" />
            <Checkbox checked={settings.heart} onChange={e => handleChange('heart', e.target.checked)} />
          </div>
          <div className='flex items-center gap-2 justify-center'>
            <FontAwesomeIcon icon={faThumbsDown} className="text-blue-500 size-5" />
            <Checkbox checked={settings.dislike} onChange={e => handleChange('dislike', e.target.checked)} />
          </div>
          <div className='flex items-center gap-2 justify-center'>
            <FontAwesomeIcon icon={faCopy} className="size-5" />
            <Checkbox checked={settings.copy} onChange={e => handleChange('copy', e.target.checked)} />
          </div>
        </div>
      </div>

      {/* Row: Button */}
      <div className="flex gap-4 mt-4 justify-center grid grid-cols-3">
        <button
          onClick={handleDefaultClick}
          disabled={isCurrentSettingsDefault()}
          className={`text-[#3f3e3e] py-2 col-span-1 flex items-center justify-center text-sm font-medium rounded-md ${
            isCurrentSettingsDefault() 
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
              : 'bg-white border border-[#F2F2F2] hover:bg-[#F2F2F2]'
          }`}
        >
          <FormattedMessage id="AGENTS.HOME.DEFAULT" />
        </button>
        <BtnSubmitAuth
          onClick={handleSave}
          disabled={!hasChanges}
          className={`px-6 py-2 rounded-[4px] font-bold col-span-2 flex items-center justify-center gap-2 ${hasChanges ? 'bg-[#00B14F]' : 'bg-gray-400 hover:bg-gray-400'} rounded-md`}
        >
          <KeenIcon icon="save-2" />
          {hasChanges ? <FormattedMessage id="AGENTS.HOME.SAVE_CHANGES" /> : <FormattedMessage id="AGENTS.HOME.SAVED" />}
        </BtnSubmitAuth>
      </div>

      {/* Default Confirmation Modal */}
      <AntdModal
        title={<FormattedMessage id="AGENTS.HOME.CONFIRM_DEFAULT" defaultMessage="Confirm Reset to Default" />}
        open={showDefaultConfirmModal}
        onOk={handleDefaultConfirm}
        onCancel={handleDefaultCancel}
        okText={<FormattedMessage id="AGENTS.HOME.CONFIRM" defaultMessage="Confirm" />}
        cancelText={<FormattedMessage id="AGENTS.HOME.CANCEL" defaultMessage="Cancel" />}
        okButtonProps={{ 
          style: { backgroundColor: '#00B14F', borderColor: '#00B14F' },
          className: 'bg-[#00B14F] border-[#00B14F] hover:bg-[#00B14F] hover:border-[#00B14F]'
        }}
      >
        <p>
          <FormattedMessage 
            id="AGENTS.HOME.DEFAULT_CONFIRM_MESSAGE" 
            defaultMessage="Are you sure you want to reset all advanced settings to default values? This action will update your agent configuration and cannot be undone." 
          />
        </p>
      </AntdModal>
    </div>
  );
};

export { AgentAdvancedSettings };
